// Pure placement and timing logic for components/ui/Tooltip.js. No DOM access here,
// so it can be unit tested with node:test.

export const HOVER_DELAY_MS = 150;
export const SKIP_WINDOW_MS = 500;
export const CLOSE_GRACE_MS = 80;
export const GUTTER = 8;
export const GAP = 8;

const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/** Raw (unclamped) top/left of the tooltip for a given side. */
const rawPosition = (side, t, tip, gap) => {
    switch (side) {
        case 'bottom':
            return { top: t.top + t.height + gap, left: t.left + t.width / 2 - tip.width / 2 };
        case 'left':
            return { top: t.top + t.height / 2 - tip.height / 2, left: t.left - tip.width - gap };
        case 'right':
            return { top: t.top + t.height / 2 - tip.height / 2, left: t.left + t.width + gap };
        default:
            return { top: t.top - tip.height - gap, left: t.left + t.width / 2 - tip.width / 2 };
    }
};

/** True when the tooltip fits on the given side along the main axis. */
const fits = (side, pos, tip, viewport, gutter) => {
    if (side === 'top') return pos.top >= gutter;
    if (side === 'bottom') return pos.top + tip.height <= viewport.height - gutter;
    if (side === 'left') return pos.left >= gutter;
    return pos.left + tip.width <= viewport.width - gutter;
};

/**
 * Where to put a fixed-position tooltip.
 * @param {{trigger:{top:number,left:number,width:number,height:number}, tip:{width:number,height:number},
 *   viewport:{width:number,height:number}, side?:'top'|'bottom'|'left'|'right', gap?:number, gutter?:number}} args
 * @returns {{top:number,left:number,side:string}} Flipped to the opposite side when the preferred one
 *   overflows and the opposite fits; clamped to the viewport with a gutter on the cross axis.
 */
export const computePlacement = ({ trigger, tip, viewport, side = 'top', gap = GAP, gutter = GUTTER }) => {
    let used = side;
    let pos = rawPosition(side, trigger, tip, gap);
    if (!fits(side, pos, tip, viewport, gutter)) {
        const flipped = rawPosition(OPPOSITE[side], trigger, tip, gap);
        if (fits(OPPOSITE[side], flipped, tip, viewport, gutter)) {
            used = OPPOSITE[side];
            pos = flipped;
        }
    }
    const maxLeft = Math.max(gutter, viewport.width - tip.width - gutter);
    const maxTop = Math.max(gutter, viewport.height - tip.height - gutter);
    return { side: used, top: clamp(pos.top, gutter, maxTop), left: clamp(pos.left, gutter, maxLeft) };
};

/**
 * Shared "skip-delay" state: once one tooltip has been open, neighbours open instantly for a short while.
 * @param {() => number} [clock] Time source in ms, injectable for tests.
 */
export const createTooltipGroup = (clock = () => Date.now()) => {
    let openCount = 0;
    let lastClosedAt = -Infinity;
    return {
        /** Delay before opening, and whether the delay was skipped (so the caller can skip the animation). */
        delayFor(reason) {
            if (reason === 'focus') return { delay: 0, skipped: false };
            if (openCount > 0 || clock() - lastClosedAt < SKIP_WINDOW_MS) return { delay: 0, skipped: true };
            return { delay: HOVER_DELAY_MS, skipped: false };
        },
        opened() {
            openCount += 1;
        },
        closed() {
            openCount = Math.max(0, openCount - 1);
            lastClosedAt = clock();
        },
    };
};

/** Module-level group shared by every Tooltip; no provider needed. */
export const tooltipGroup = createTooltipGroup();
